import {
  Users,
  Lock,
  ArrowRight,
  Eye,
  EyeOff,
  ShieldCheck,
  Loader2,
  AlertCircle,
  LayoutDashboard,
  Building2,
} from "lucide-react";
import { useOneDeskLogin } from "./hooks/useOneDeskLogin";
import { Input, Button } from "@repo/core-ui"; // Using component-based UI
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const OneDeskLogin = () => {
  const navigate = useNavigate();
  const {
    formData,
    isLoading,
    showPassword,
    setShowPassword,
    handleChange,
    handleLogin,
  } = useOneDeskLogin();

  return (
    <div className="min-h-screen w-full bg-[#FAFAFA] flex items-center justify-center p-4 font-sans overflow-hidden relative">
      {/* Ambient Background Glows - Reduced Intensity */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-20%] left-[-10%] w-[40%] h-[40%] bg-blue-500/5 blur-[120px] rounded-full mix-blend-multiply animate-pulse-slow" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[40%] h-[40%] bg-indigo-500/5 blur-[120px] rounded-full mix-blend-multiply animate-pulse-slow delay-1000" />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-[900px] h-auto min-h-[500px] bg-white text-sm md:text-base rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.08)] border border-white/60 relative z-10 flex overflow-hidden ring-1 ring-black/[0.03]"
      >
        {/* Left Side: Visual/Brand - Compact */}
        <div className="hidden lg:flex w-[45%] relative flex-col justify-between p-8 overflow-hidden bg-slate-900 text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,#3b82f6_0%,#1e3a8a_50%,#0f172a_100%)] opacity-90" />
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150 mix-blend-overlay" />
          <div className="absolute -top-24 -left-24 w-64 h-64 bg-blue-500/30 rounded-full blur-[60px] mix-blend-screen" />

          {/* Logo */}
          <div className="relative z-20 flex items-center gap-2.5">
            <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 shadow-inner">
              <LayoutDashboard
                size={20}
                className="text-white"
                strokeWidth={2}
              />
            </div>
            <span className="text-3xl font-bold tracking-tight text-white/95 font-['Inter']">
              OneDesk<span className="text-blue-400">.</span>
            </span>
          </div>

          {/* Main Content */}
          <div className="relative z-20 space-y-4 my-auto">
            <motion.h1
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl font-bold leading-tight tracking-tight"
            >
              Manage All <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200">
                Hotel & Security
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-sm text-blue-100/70 font-medium leading-relaxed max-w-sm"
            >
              Central command for RoooMix. Scale operations with precision.
            </motion.p>
          </div>

          {/* Footer Minimal */}
          <div className="relative z-20 text-xs text-blue-100/40 font-medium">
            &copy; {new Date().getFullYear()} RoooMix Inc.
          </div>
        </div>

        {/* Right Side: Login Form - Compact */}
        <div className="w-full lg:w-[55%] bg-white flex flex-col justify-center px-8 md:px-12 py-10 relative">
          <div className="w-full max-w-sm mx-auto space-y-5">
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-2"
            >
              <div className="flex justify-center mb-8">
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="relative group cursor-pointer"
                >
                  {/* Animated Glow Background */}
                  <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 rounded-[2rem] blur-xl opacity-0 group-hover:opacity-100 transition duration-500"></div>

                  {/* Glassmorphic Container */}
                  <div className="relative flex items-center justify-center min-w-[120px]">
                    <img
                      className="h-20 md:h-24 w-auto object-contain brightness-110 contrast-110"
                      src="/logo.png"
                      alt="OneDesk Logo"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (target.src.includes("/logo.png")) {
                          target.src = "public/logo.png";
                        }
                      }}
                    />
                  </div>
                </motion.div>
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-[12px] font-bold uppercase tracking-wider">
                <ShieldCheck size={12} className="fill-blue-600/20" />
                <span>Secured Access</span>
              </div>
              <p className="text-slate-500 text-sm font-medium">
                Enter your credentials to access the dashboard.
              </p>
            </motion.div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-3">
                <Input
                  label="Username"
                  type="text"
                  name="Username"
                  placeholder="username"
                  className="!py-2.5 text-sm bg-white border-slate-200 focus:border-blue-600 focus:ring-blue-600/5"
                  containerClassName="!mb-0"
                  prefix={<Users size={16} className="text-slate-400" />}
                  value={formData.Username}
                  onChange={handleChange}
                />

                <Input
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  name="Password"
                  placeholder="••••••••"
                  className="!py-2.5 text-sm bg-white border-slate-200 focus:border-blue-600 focus:ring-blue-600/5"
                  containerClassName="!mb-0"
                  prefix={<Lock size={16} className="text-slate-400" />}
                  suffix={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="hover:text-blue-600 transition-colors p-1"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  }
                  value={formData.Password}
                  onChange={handleChange}
                />

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500/20 transition-all cursor-pointer"
                    />
                    <span className="text-xs font-medium text-slate-500 group-hover:text-slate-700 transition-colors">
                      Remember me
                    </span>
                  </label>
                  <button
                    type="button"
                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline transition-all"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-slate-900 hover:bg-black text-white rounded-xl font-bold text-sm shadow-md shadow-black/10 hover:shadow-lg hover:shadow-black/20 transition-all duration-300 group"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="animate-spin" size={16} />
                    <span>Verifying...</span>
                  </div>
                ) : (
                  <span className="flex items-center gap-2 group-hover:gap-2 transition-all">
                    Sign In
                    <ArrowRight size={16} className="opacity-70" />
                  </span>
                )}
              </Button>
            </form>

            <div className="pt-6 border-t border-slate-100 flex flex-col gap-4">
              <Button
                type="button"
                onClick={() => navigate('/register-hotel')}
                variant="outline"
                className="w-full h-11 bg-white hover:bg-slate-50 text-slate-700 border-slate-200 rounded-xl font-bold text-sm shadow-sm hover:shadow-md transition-all duration-300 group"
              >
                <span className="flex items-center gap-2">
                  <Building2 size={16} className="text-blue-600" />
                  Register New Property
                </span>
              </Button>
            
              <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400 font-medium">
                <AlertCircle size={12} className="text-slate-300" />
                <p>Encrypted connection. Authorized personnel only.</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default OneDeskLogin;
